// ============================================
// LIGA AJUPAM - Public API Service (v6.4.0)
// ============================================
// Servicios para vistas públicas de la liga.
// No requieren autenticación de participante ni admin.

import { supabase } from './client';
import { s3ImageProxyUrl } from './storage';
import { withTimeout } from '../../utils/withTimeout';
import { resolveZoneCuts, type ZoneCuts } from '../../utils/zoneCuts';
import type {
  League,
  PublicCategory,
  PublicStandingsEntry,
  PublicMatchWithResult,
  PublicTeam,
  PreRegistration,
  LatestResult,
  LiveMatch,
} from '../../types';

// ============================================
// Liga
// ============================================

/**
 * Obtener la "liga actual" para flujos sin contexto explícito.
 *
 * v6.7.21: el RPC devuelve la única liga 'enabled' si hay exactamente 1.
 * Si hay 0 o ≥2, devuelve null y el caller debe ofrecer selector
 * (LandingPage) o resolver por otro medio (slug en URL, team del usuario).
 *
 * El fallback histórico que filtraba por `is_current=true` se eliminó porque
 * la columna ya no existe (migración v6.7.21).
 */
export async function getCurrentLeague(): Promise<League | null> {
  try {
    // Timeout: esta resolución alimenta el splash de arranque; sin acotar, un RPC
    // colgado dejaba `settings` sin resolver y el boot "pensando" para siempre.
    const { data, error } = await withTimeout(supabase.rpc('get_current_league'), 15000, 'get_current_league');

    if (error) {
      console.error('Error fetching current league:', error);
      return null;
    }

    // El RPC devuelve SETOF leagues; supabase-js retorna array.
    if (Array.isArray(data)) return data[0] ?? null;
    return data ?? null;
  } catch (e) {
    console.error('get_current_league timeout/failed:', e);
    return null;
  }
}

/**
 * v6.11.28.02 — Liga ACTIVA de una familia ("default por familia").
 *
 * `getCurrentLeague()` razona sobre TODA la base y se rinde (null) cuando hay 0 o
 * >=2 ligas 'enabled'. Con multi-región eso pasa siempre: hoy hay 3 familias con
 * una liga activa cada una. La regla que sí escala es por familia — una familia
 * tiene como mucho UNA liga activa a la vez — y es lo que resuelve este RPC.
 *
 * Se le puede pasar la familia directo (`familySlug`) o cualquier liga de esa
 * familia (`referenceLeagueId`), que es lo cómodo desde el front: casi siempre
 * tenemos a mano un league_id (el del equipo) y no el family_slug.
 *
 * Devuelve null si no se pudo resolver la familia o si esa familia no tiene liga
 * activa (entre temporadas). No lanza: los callers son de UI y degradan.
 */
export async function getCurrentLeagueByFamily(
  opts: { familySlug?: string | null; referenceLeagueId?: string | null }
): Promise<League | null> {
  try {
    // El RPC es nuevo y todavía no está en los tipos generados de Supabase, así
    // que la llamada va por `as any` y el resultado se anota a mano.
    const { data, error } = await withTimeout<{ data: League[] | League | null; error: unknown }>(
      (supabase.rpc as any)('get_current_league_by_family', {
        p_family_slug: opts.familySlug ?? null,
        p_reference_league_id: opts.referenceLeagueId ?? null,
      }),
      15000,
      'get_current_league_by_family',
    );
    if (error) {
      console.error('Error fetching current league by family:', error);
      return null;
    }
    // SETOF leagues → supabase-js retorna array.
    if (Array.isArray(data)) return (data[0] ?? null) as League | null;
    return (data ?? null) as League | null;
  } catch (e) {
    console.error('get_current_league_by_family timeout/failed:', e);
    return null;
  }
}

/**
 * v6.5.0 Fase 4 — Ligas visibles al público.
 * v6.7.20 — Modelo simplificado: el RPC sólo devuelve ligas con
 *           status='enabled' AND pre_registration_enabled=true.
 *           Los flags is_registration_active / is_season_active vienen de
 *           league_settings y permiten armar el label correcto en la UI
 *           ("Inscripción abierta", "En curso", etc.).
 */
export interface PublicLeagueSummary {
  league_id: string;
  name: string;
  slug: string;
  family_slug: string;
  family_name: string;
  edition_number: number | null;
  status: 'enabled';
  hero_image_url: string | null;
  is_registration_active: boolean;
  is_season_active: boolean;
}

export async function getPublicPreRegistrationLeagues(): Promise<PublicLeagueSummary[]> {
  try {
    const { data, error } = await withTimeout(
      supabase.rpc('get_public_pre_registration_leagues'), 15000, 'get_public_pre_registration_leagues',
    );
    if (error) {
      console.error('Error fetching public leagues:', error);
      return [];
    }
    return (data ?? []) as PublicLeagueSummary[];
  } catch (e) {
    console.error('get_public_pre_registration_leagues timeout/failed:', e);
    return [];
  }
}

/**
 * v6.10.0.28 — Directorio público de ligas para la landing "entre temporadas"
 * (cuando no hay ninguna liga 'enabled'). Devuelve:
 *  - enabled : ligas habilitadas (para que el FE distinga 0 / 1 / N enabled).
 *  - upcoming: ligas 'draft' con fecha de inicio configurada → "próximas".
 *  - past    : ligas 'archived' → galería de ediciones finalizadas (read-only
 *              por slug en /liga/:slug, ya soportado).
 */
export interface PublicDirectoryLeague {
  league_id: string;
  name: string;
  slug: string;
  family_slug: string;
  family_name: string;
  edition_number: number | null;
  hero_image_url: string | null;
  regular_phase_start_date?: string | null;
  regular_phase_end_date?: string | null;
  finals_start_date?: string | null;
  finals_end_date?: string | null;
  is_registration_active?: boolean;
  /**
   * v6.11.135 — Lo devuelve el bloque `enabled` del directorio. El selector de
   * `/` lo necesita para distinguir "inscribiendo" de "jugándose" cuando hay 2
   * o más habilitadas. Opcional porque `upcoming` y `past` no lo traen.
   */
  is_season_active?: boolean;
  pre_registration_enabled?: boolean;
}

export interface PublicLeagueDirectory {
  enabled: PublicDirectoryLeague[];
  upcoming: PublicDirectoryLeague[];
  past: PublicDirectoryLeague[];
}

// ============================================
// v6.11.14.00 — Muro de la Fama global
// ============================================

export interface HallOfFameTitle {
  league_slug: string;
  league_name: string;
  edition_number: number | null;
  year: number | null;
  category_name: string;
  category_branch: string;
  team_id: string;
  team_name: string;
  teammate_name: string;
  teammate_photo_url: string | null;
  own_photo_url: string | null;
  journey_url: string;
  role?: 'CHAMPION' | 'RUNNER_UP'; // v6.11.15.08
}

export interface HallOfFamePerson {
  person_key: string;
  display_name: string;
  photo_url: string | null;
  titles_count: number;
  champion_count?: number;   // v6.11.15.08
  runner_up_count?: number;  // v6.11.15.08
  titles: HallOfFameTitle[];
}

/**
 * v6.11.15.01 — Resolver de slug histórico → slug vigente. Si el slug fue
 * renombrado desde el superadmin, devolvemos el nuevo para emitir un redirect
 * desde el frontend. Devuelve null si no hay match.
 */
export async function resolveLeagueSlugRedirect(oldSlug: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('resolve_league_slug_redirect', {
    p_old_slug: oldSlug,
  });
  if (error) {
    console.error('Error resolving slug redirect:', error);
    return null;
  }
  if (typeof data === 'string' && data.length > 0) return data;
  return null;
}

/**
 * Muro de la Fama global (cross-ediciones), dedupeado por persona.
 * Un jugador que ganó 2 ediciones aparece una única vez con titles.length === 2.
 */
export async function getHallOfFame(): Promise<HallOfFamePerson[]> {
  const { data, error } = await supabase.rpc('get_public_hall_of_fame');
  if (error) {
    console.error('Error fetching hall of fame:', error);
    return [];
  }
  // El RPC devuelve SETOF jsonb → supabase-js retorna array de objetos.
  if (!Array.isArray(data)) return [];
  return data as HallOfFamePerson[];
}

export async function getPublicLeagueDirectory(): Promise<PublicLeagueDirectory> {
  const { data, error } = await supabase.rpc('get_public_league_directory');
  if (error) {
    console.error('Error fetching public league directory:', error);
    return { enabled: [], upcoming: [], past: [] };
  }
  const d = (data ?? {}) as Partial<PublicLeagueDirectory>;
  return {
    enabled: d.enabled ?? [],
    upcoming: d.upcoming ?? [],
    past: d.past ?? [],
  };
}

/**
 * v6.5.0 Fase 5 — Obtener una liga por slug (landing pública /liga/:slug).
 * Fallback directo a la tabla `leagues` ya que no hay RPC dedicado.
 */
export async function getLeagueBySlug(slug: string): Promise<League | null> {
  try {
    const { data, error } = await withTimeout(
      supabase.from('leagues').select('*').eq('slug', slug).maybeSingle(),
      15000, 'getLeagueBySlug',
    );
    if (error) {
      console.error('Error fetching league by slug:', error);
      return null;
    }
    return data as League | null;
  } catch (e) {
    console.error('getLeagueBySlug timeout/failed:', e);
    return null;
  }
}

/**
 * v6.5.0 Fase 7.5 — Reglamento público por slug de liga.
 * Devuelve el markdown publicado + placeholders resueltos server-side.
 */
export interface PublicRulebookPayload {
  league: {
    name: string;
    slug: string;
    family_name: string;
    edition_number: number | null;
  };
  rulebook: {
    version: number;
    major_version: number | null;
    minor_version: number | null;
    published_at: string;
    published_pdf_url: string | null;
    body: string;
  } | null;
  placeholders: Record<string, string>;
}

export async function getPublicRulebook(slug: string): Promise<PublicRulebookPayload | null> {
  const { data, error } = await supabase.rpc('get_public_rulebook', {
    p_league_slug: slug,
  });
  if (error) {
    console.error('Error fetching public rulebook:', error);
    return null;
  }
  return (data ?? null) as PublicRulebookPayload | null;
}

/**
 * Obtener categorías de una liga con conteos
 */
export async function getPublicLeagueCategories(leagueSlug?: string): Promise<PublicCategory[]> {
  // Intentar RPC primero
  const { data, error } = await supabase
    .rpc('get_public_league_categories', {
      p_league_slug: leagueSlug || null,
    });

  if (error) {
    console.error('Error fetching public categories:', error);
    // Fallback: query directa
    const { data: categories } = await supabase
      .from('tournament_categories')
      .select('id, name, branch, is_active, current_teams, max_zones')
      .eq('is_active', true)
      .order('name');

    if (!categories) return [];

    return categories.map((c) => ({
      id: c.id,
      name: c.name,
      branch: c.branch,
      team_count: c.current_teams || 0,
      match_count: 0,
      finished_match_count: 0,
      zones: [],
    }));
  }

  // El RPC devuelve `zones: null` para categorías sin equipos/zonas (p.ej. edición
  // plantilla sin inscriptos). Normalizamos a [] para que los consumidores no
  // rompan al hacer `.length` (crash histórico en LeagueView con la 5ta Edición).
  return ((data as PublicCategory[] | null) || []).map((c) => ({
    ...c,
    zones: c.zones ?? [],
  }));
}

// ============================================
// Posiciones
// ============================================

/**
 * Obtener posiciones públicas por categoría y zona
 */
export async function getPublicCategoryStandings(
  categoryId: string,
  zoneGroup?: string
): Promise<PublicStandingsEntry[]> {
  const { data, error } = await supabase
    .rpc('get_public_category_standings', {
      p_category_id: categoryId,
      p_zone_group: zoneGroup || null,
    });

  if (error) {
    console.error('Error fetching public standings:', error);
    return [];
  }

  return data || [];
}

/**
 * Cortes de clasificación de una categoría (directos / total) para marcar la
 * tabla pública. `tournament_categories` y `league_settings` tienen lectura
 * pública, así que no hace falta RPC. `null` si no se puede resolver: la tabla
 * se muestra entonces sin marcas.
 */
export async function getPublicCategoryCuts(categoryId: string): Promise<ZoneCuts | null> {
  try {
    const { data: cat, error: catError } = await supabase
      .from('tournament_categories')
      .select('league_id, direct_to_finals_per_zone')
      .eq('id', categoryId)
      .maybeSingle();
    if (catError || !cat?.league_id) return null;

    const { data: settings, error: settingsError } = await supabase
      .from('league_settings')
      .select('teams_qualifying_to_finals, default_direct_to_finals_per_zone')
      .eq('league_id', cat.league_id)
      .maybeSingle();
    if (settingsError || !settings) return null;

    return resolveZoneCuts(
      cat.direct_to_finals_per_zone,
      settings.default_direct_to_finals_per_zone,
      settings.teams_qualifying_to_finals
    );
  } catch (e) {
    console.error('Error fetching public category cuts:', e);
    return null;
  }
}

// ============================================
// Partidos
// ============================================

/**
 * Obtener partidos públicos de una categoría
 */
export async function getPublicCategoryMatches(
  categoryId: string,
  status?: string
): Promise<PublicMatchWithResult[]> {
  const { data, error } = await supabase
    .rpc('get_public_category_matches', {
      p_category_id: categoryId,
      p_status: status || null,
    });

  if (error) {
    console.error('Error fetching public matches:', error);
    return [];
  }

  return data || [];
}

// ============================================
// Equipos
// ============================================

/**
 * Obtener detalle público de un equipo
 */
export async function getPublicTeamDetail(teamId: string): Promise<PublicTeam | null> {
  const { data, error } = await supabase
    .rpc('get_public_team_detail', {
      p_team_id: teamId,
    });

  if (error) {
    console.error('Error fetching public team detail:', error);
    return null;
  }

  // RPC retorna array, tomamos el primero
  return Array.isArray(data) ? data[0] || null : data;
}

// ============================================
// Pre-inscripción
// ============================================

/**
 * Enviar pre-inscripción a una liga
 */
export async function submitPreRegistration(
  registration: PreRegistration,
  captchaToken?: string
): Promise<{ success: boolean; message: string }> {
  const { data, error } = await supabase
    .rpc('submit_pre_registration', {
      p_full_name: registration.full_name,
      p_phone: registration.phone,
      p_dni: registration.dni,
      p_birth_date: registration.birth_date || null,
      p_preferred_category: registration.preferred_category || null,
      p_captcha_token: captchaToken || null,
      p_edition_location: registration.edition_location || null,
      // v6.7.20: pasamos la liga elegida en el form. Antes se ignoraba (bug
      // pre-existente desde v6.5.0 Fase 4) y el RPC defaulteaba a is_current.
      p_league_id: registration.league_id || null,
    });

  if (error) {
    console.error('Error submitting pre-registration:', error);
    // Errores conocidos
    if (error.message?.includes('already registered') || error.message?.includes('ya registrado')) {
      return { success: false, message: 'Este DNI ya está registrado como participante o pre-inscripto.' };
    }
    return { success: false, message: error.message || 'Error al enviar pre-inscripción.' };
  }

  return { success: true, message: 'Pre-inscripción enviada correctamente.' };
}

// ============================================
// Captación de leads en 2 pasos (v6.11.5)
// ============================================
// Paso 1: captura ligera (nombre + WhatsApp). Paso 2 (opcional): ficha con DNI.
// Ambos pasan por la Edge Function `submit-lead` (Turnstile + IP + geo).

export type LeadSource = 'post_finals' | 'between_seasons' | 'landing';

export interface SubmitLeadResult {
  success: boolean;
  lead_id?: string;
  stage?: 'lead' | 'complete';
  deduped?: boolean;
  error?: string;
}

/**
 * Paso 1 — Captura ligera de lead (nombre + WhatsApp).
 */
export async function submitLead(
  params: {
    full_name: string;
    phone: string;
    source?: LeadSource;
    league_id?: string | null;
    league_slug?: string | null;
  },
  captchaToken: string,
): Promise<SubmitLeadResult> {
  const { data, error } = await supabase.functions.invoke('submit-lead', {
    body: {
      action: 'lead',
      full_name: params.full_name,
      phone: params.phone,
      source: params.source || 'landing',
      league_id: params.league_id || null,
      league_slug: params.league_slug || null,
      captcha_token: captchaToken,
    },
  });

  if (error) return { success: false, error: error.message };
  if (data && !data.success) return { success: false, error: data.error || 'Error al enviar.' };
  return data as SubmitLeadResult;
}

/**
 * Paso 2 (opcional) — Ascender un lead a ficha completa (DNI + categoría + fecha).
 */
export async function completeLead(
  params: {
    lead_id: string;
    dni: number;
    birth_date?: string;
    preferred_category?: string;
  },
  captchaToken: string,
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke('submit-lead', {
    body: {
      action: 'complete',
      lead_id: params.lead_id,
      dni: params.dni,
      birth_date: params.birth_date || null,
      preferred_category: params.preferred_category || null,
      captcha_token: captchaToken,
    },
  });

  if (error) return { success: false, error: error.message };
  if (data && !data.success) return { success: false, error: data.error || 'Error al completar la ficha.' };
  return { success: true };
}

// ============================================
// Feed unificado regular + finals (v6.9.2 Fase A)
// ============================================

export interface HomepageFeedItem {
  id: string;
  match_kind: 'REGULAR' | 'FINALS';
  match_phase: string | null;
  date: string;
  scheduled_time: string | null;
  venue_name: string | null;
  court_number: number | null;
  team_a_id: string | null;
  team_a_name: string;
  team_a_captain: string | null;
  team_a_partner: string | null;
  team_b_id: string | null;
  team_b_name: string;
  team_b_captain: string | null;
  team_b_partner: string | null;
  team_a_tbd: string | null;
  team_b_tbd: string | null;
  /** v6.9.2 — Posición de zona pre-playoffs ("3B" = 3° de Zona B). Sólo finals con FQT poblado. */
  team_a_zone_rank: string | null;
  team_b_zone_rank: string | null;
  category_name: string | null;
  branch: string | null;
  bracket_label: string | null;
  initial_photo_url: string | null;
  is_upset: boolean;
  // Sólo en results
  result_set1_a?: number | null;
  result_set1_b?: number | null;
  result_set2_a?: number | null;
  result_set2_b?: number | null;
  result_set3_a?: number | null;
  result_set3_b?: number | null;
  team_a_sets?: number | null;
  team_b_sets?: number | null;
  is_super_tie_break?: boolean | null;
  finished_at?: string;
  scheduled_at?: string;
}

export interface HomepageFeed {
  live: HomepageFeedItem[];
  upcoming: HomepageFeedItem[];
  results: HomepageFeedItem[];
  now: string;
}

export async function getHomepageFeed(
  leagueId?: string,
  limitLive: number = 8,
  limitResults: number = 12
): Promise<HomepageFeed> {
  const { data, error } = await supabase.rpc('get_homepage_feed', {
    p_league_id: leagueId ?? null,
    p_limit_live: limitLive,
    p_limit_results: limitResults,
  });

  if (error) {
    console.error('Error fetching homepage feed:', error);
    return { live: [], upcoming: [], results: [], now: new Date().toISOString() };
  }

  return (data ?? { live: [], upcoming: [], results: [], now: new Date().toISOString() }) as HomepageFeed;
}

export interface RecentFinalsPhoto {
  photo_id: string;
  finals_match_id: string;
  url: string;
  kind: 'INICIAL' | 'EXTRA';
  taken_at: string;
  match_phase: string;
  bracket_label: string | null;
  category_name: string;
  branch: string;
  team_a_id: string | null;
  team_a_name: string;
  team_b_id: string | null;
  team_b_name: string;
  match_status: string;
  team_a_sets: number | null;
  team_b_sets: number | null;
}

export async function getRecentFinalsPhotos(
  leagueId?: string,
  limit: number = 24
): Promise<RecentFinalsPhoto[]> {
  const { data, error } = await supabase.rpc('get_recent_finals_photos', {
    p_league_id: leagueId ?? null,
    p_limit: limit,
  });
  if (error) {
    console.error('Error fetching recent finals photos:', error);
    return [];
  }
  return (data ?? []) as RecentFinalsPhoto[];
}

// ============================================
// Bracket público (v6.9.2 Fase D)
// ============================================

export interface PublicBracketMatch {
  match_id: string;
  category_id: string;
  category_name: string;
  branch: string;
  match_phase: string;
  bracket_label: string | null;
  match_code: string | null;
  bracket_position: number | null;
  match_order: number | null;
  status: string;
  scheduled_date: string | null;
  venue_name: string | null;
  court_number: number | null;
  team_a_id: string | null;
  team_a_name: string | null;
  team_a_zone: string | null;
  team_a_position: number | null;
  team_b_id: string | null;
  team_b_name: string | null;
  team_b_zone: string | null;
  team_b_position: number | null;
  team_a_sets: number | null;
  team_b_sets: number | null;
  result_set1_a: number | null;
  result_set1_b: number | null;
  result_set2_a: number | null;
  result_set2_b: number | null;
  result_set3_a: number | null;
  result_set3_b: number | null;
  tbd_description_a: string | null;
  tbd_description_b: string | null;
  depends_on_matches: any;
  initial_photo_url: string | null;
}

export async function getPublicPlayoffBracket(
  categoryId: string,
  leagueId?: string
): Promise<PublicBracketMatch[]> {
  const { data, error } = await supabase.rpc('get_playoff_bracket_for_category', {
    p_category_id: categoryId,
    p_league_id: leagueId ?? null,
  });
  if (error) {
    console.error('Error fetching public bracket:', error);
    return [];
  }
  return (data ?? []) as PublicBracketMatch[];
}

// ============================================
// Tournament Highlights (v6.9.2 Fase H)
// ============================================

export type HighlightKind =
  | 'UPSET'
  | 'GIANT_FALLS'
  | 'CINDERELLA'
  | 'SUPER_TB_FINAL'
  | 'REMATCH';

export interface TournamentHighlight {
  kind: HighlightKind;
  priority: number;
  title: string;
  subtitle: string;
  match_id: string;
  category_id: string;
  category_name: string;
  branch: string;
  match_phase: string;
  team_a_id: string;
  team_a_name: string;
  team_b_id: string;
  team_b_name: string;
  winner_team_id: string | null;
  score_summary: string;
  photo_url: string | null;
  created_at: string;
  // v6.9.2 Fase H+: metadata del match (hora prevista, inicio, fin, sede, cancha)
  scheduled_date: string | null;
  match_started_at: string | null;
  match_updated_at: string | null;
  venue_name: string | null;
  court_number: number | null;
}

/**
 * Tipos de destacado que la liga NO muestra públicamente. Los 6-0 («rosca»)
 * los calcula el RPC, pero no se destacan: dan a entender falta de control en
 * las categorías. Se filtran acá, único punto de consumo (TV, web y pantallas).
 */
const HIDDEN_HIGHLIGHT_KINDS = new Set(['BAGEL']);

export async function getTournamentHighlights(
  leagueId?: string,
  limit: number = 12
): Promise<TournamentHighlight[]> {
  // Se piden filas de más para que, al descartar los tipos ocultos, la lista
  // siga llegando a `limit`.
  const { data, error } = await supabase.rpc('get_tournament_highlights', {
    p_league_id: leagueId ?? null,
    p_limit: limit * 2,
  });
  if (error) {
    console.error('Error fetching tournament highlights:', error);
    return [];
  }
  return ((data ?? []) as TournamentHighlight[])
    .filter((h) => !HIDDEN_HIGHLIGHT_KINDS.has(h.kind as string))
    .slice(0, limit);
}

// ============================================
// Team Finals Photos (v6.9.2 Fase E)
// ============================================

export interface TeamFinalsPhoto {
  photo_id: string;
  finals_match_id: string;
  url: string;
  kind: 'INICIAL' | 'EXTRA';
  taken_at: string;
  match_phase: string;
  bracket_label: string | null;
  category_name: string;
  opponent_team_id: string | null;
  opponent_team_name: string | null;
  match_status: string;
  match_score: string | null;
}

export async function getTeamFinalsPhotos(
  teamId: string,
  limit: number = 30
): Promise<TeamFinalsPhoto[]> {
  const { data, error } = await supabase.rpc('get_team_finals_photos', {
    p_team_id: teamId,
    p_limit: limit,
  });
  if (error) {
    console.error('Error fetching team finals photos:', error);
    return [];
  }
  return (data ?? []) as TeamFinalsPhoto[];
}

// ============================================
// Champion / Runner-up Journey (v6.9.2 Fase I)
// ============================================

export interface FinalistEntry {
  category_id: string;
  category_name: string;
  branch: string;
  final_match_id: string;
  champion_team_id: string;
  champion_team_name: string;
  champion_captain_name: string | null;
  champion_partner_name: string | null;
  runner_up_team_id: string;
  runner_up_team_name: string;
  runner_up_captain_name: string | null;
  runner_up_partner_name: string | null;
  final_score: string;
  final_photo_url: string | null;
  finished_at: string;
}

export async function getFinalistsByLeague(leagueId?: string): Promise<FinalistEntry[]> {
  const { data, error } = await supabase.rpc('get_finalists_by_league', {
    p_league_id: leagueId ?? null,
  });
  if (error) {
    console.error('Error fetching finalists:', error);
    return [];
  }
  const entries = (data ?? []) as FinalistEntry[];
  // v6.10.0.31: final_photo_url es presigned S3 (bucket privado, vence a los 7
  // días). Reescribir a la URL de proxy estable (no expira) — sync, sin red.
  for (const e of entries) {
    if (e.final_photo_url) e.final_photo_url = s3ImageProxyUrl(e.final_photo_url);
  }
  return entries;
}

export interface ChampionJourney {
  team: {
    id: string;
    name: string;
    captain_name: string | null;
    captain_photo: string | null;
    captain_instagram: string | null;
    partner_name: string | null;
    partner_photo: string | null;
    partner_instagram: string | null;
  };
  category: { id: string; name: string; branch: string };
  title_role: 'CHAMPION' | 'RUNNER_UP' | null;
  regular_stats: {
    matches_played: number;
    matches_won: number;
    matches_lost: number;
    sets_won: number;
    sets_lost: number;
    set_difference: number;
    games_won: number;
    games_lost: number;
    game_difference: number;
    final_position: number | null;
    points: number;
  };
  first_match: any | null;
  playoff_journey: Array<{
    match_id: string;
    match_phase: string;
    bracket_label: string | null;
    match_code: string | null;
    opponent_id: string | null;
    opponent_name: string | null;
    team_a_sets: number | null;
    team_b_sets: number | null;
    won: boolean;
    is_super_tie_break: boolean | null;
    three_sets: boolean;
    photo_url: string | null;
    score: string;
    scheduled_date: string | null;
    status: string;
  }>;
  all_photos: Array<{
    photo_id: string;
    url: string;
    kind: 'INICIAL' | 'EXTRA';
    taken_at: string;
    finals_match_id: string;
    match_phase: string;
    opponent_name: string | null;
  }>;
  trophy_match: {
    match_id: string;
    team_a_sets: number | null;
    team_b_sets: number | null;
    photo_url: string | null;
    scheduled_date: string | null;
  } | null;
  error?: string;
}

export async function getChampionJourney(
  teamId: string,
  leagueId?: string
): Promise<ChampionJourney | null> {
  const { data, error } = await supabase.rpc('get_champion_journey', {
    p_team_id: teamId,
    p_league_id: leagueId ?? null,
  });
  if (error) {
    console.error('Error fetching champion journey:', error);
    return null;
  }
  const journey = data as ChampionJourney | null;
  // El RPC no siempre trae los arrays: en la respuesta de error ({ error }) y en
  // equipos sin recorrido de playoffs vienen ausentes, no vacíos. Los consumidores
  // los dereferencian en useMemo/render ANTES de poder chequear `error` (los hooks
  // corren antes del early-return), así que normalizamos acá una sola vez en vez de
  // sembrar `?? []` en cada punto de uso.
  if (journey) {
    if (!Array.isArray(journey.playoff_journey)) journey.playoff_journey = [];
    if (!Array.isArray(journey.all_photos)) journey.all_photos = [];
  }
  // v6.10.0.31: las fotos de finales son presigned S3 (bucket privado, vencen a
  // los 7 días). Reescribir a la URL de proxy estable (no expira) — sync, sin red.
  if (journey && !journey.error) {
    (journey.all_photos ?? []).forEach((p) => {
      if (p.url) p.url = s3ImageProxyUrl(p.url);
    });
    (journey.playoff_journey ?? []).forEach((m) => {
      if (m.photo_url) m.photo_url = s3ImageProxyUrl(m.photo_url);
    });
    if (journey.trophy_match?.photo_url) {
      journey.trophy_match.photo_url = s3ImageProxyUrl(journey.trophy_match.photo_url);
    }
  }
  return journey;
}

// ============================================
// Últimos resultados (carrousel landing) — DEPRECATED v6.9.2
// ============================================
// Se mantienen como compat shims (algunos callers viejos podrían usarlos).
// La landing y el carousel ya consumen getHomepageFeed() directamente.

/**
 * Obtener últimos resultados confirmados para el carrousel
 * @deprecated v6.9.2 — usar getHomepageFeed()
 */
export async function getLatestResults(limit: number = 20): Promise<LatestResult[]> {
  const { data, error } = await supabase
    .rpc('get_latest_results', { p_limit: limit });

  if (error) {
    console.error('Error fetching latest results:', error);
    return [];
  }

  return data || [];
}

// ============================================
// Partidos en juego ahora
// ============================================

/**
 * Obtener partidos que deberían estar jugándose ahora
 * @deprecated v6.9.2 — usar getHomepageFeed()
 */
export async function getLiveMatches(): Promise<LiveMatch[]> {
  const { data, error } = await supabase
    .rpc('get_live_matches');

  if (error) {
    console.error('Error fetching live matches:', error);
    return [];
  }

  return data || [];
}

/**
 * Obtener próximos partidos de hoy (hora aún no llegó)
 * @deprecated v6.9.2 — usar getHomepageFeed()
 */
export async function getUpcomingToday(limit: number = 5): Promise<LiveMatch[]> {
  const { data, error } = await supabase
    .rpc('get_upcoming_today', { p_limit: limit });

  if (error) {
    console.error('Error fetching upcoming today:', error);
    return [];
  }

  return data || [];
}

// ============================================
// Zonas disponibles por categoría
// ============================================

/**
 * Obtener las zonas existentes de una categoría
 */
// ============================================
// Búsqueda global de equipos
// ============================================

export interface PublicTeamSearchResult {
  team_id: string;
  team_name: string;
  captain_name: string | null;
  partner_name: string | null;
  category_name: string;
  category_id: string;
  zone_group: string | null;
  branch: string;
}

/**
 * Buscar equipos por nombre o integrante en toda la liga.
 * Requiere RPC search_public_teams en Supabase.
 */
export async function searchPublicTeams(
  query: string,
  leagueSlug?: string
): Promise<PublicTeamSearchResult[]> {
  if (!query || query.trim().length < 2) return [];

  const { data, error } = await supabase
    .rpc('search_public_teams', {
      p_query: query.trim(),
      p_league_slug: leagueSlug || null,
    });

  if (error) {
    console.error('Error searching teams:', error);
    return [];
  }

  return (data as PublicTeamSearchResult[]) || [];
}

export async function getPublicCategoryZones(categoryId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('teams')
    .select('zone_group')
    .eq('category_id', categoryId)
    .not('zone_group', 'is', null)
    .neq('status', 'ELIMINADO');

  if (error) {
    console.error('Error fetching zones:', error);
    return [];
  }

  // Extraer zonas únicas y ordenar
  const zones = [...new Set((data || []).map((t) => t.zone_group).filter(Boolean))] as string[];
  return zones.sort();
}
